import { useEffect, useState } from "react";
import { fetchContentRecommend, isAbortError } from "./api";
import { resolveSections } from "./resolve-sections";
import type { NewsletterSection, SectionArticle } from "./segment-types";
import type { PreviewSection } from "./newsletter-html";

/**
 * Fill a newsletter's dynamic slots for one category.
 *
 * This is what a recipient in that category would actually receive. The editor
 * previews one category at a time; the send pipeline does the same resolution
 * per subscriber. Keeping the logic in one place means the preview is a real
 * rehearsal rather than an approximation.
 */
export function useResolvedSections(
  sections: NewsletterSection[],
  category: string | null,
): { resolved: PreviewSection[]; loading: boolean } {
  const [pool, setPool] = useState<SectionArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const key = category ?? "";

  useEffect(() => {
    if (!key) { setPool([]); return; }
    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await fetchContentRecommend({
          category: key, kinds: ["article", "term", "news"], signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        setPool(rows.flatMap((r, i) => (r.url
          ? [{ id: `pool-${i}`, title: r.title, url: r.url, category: r.category }]
          : [])));
      } catch (err) {
        if (!isAbortError(err) && !ac.signal.aborted) setPool([]);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => { ac.abort(); window.clearTimeout(t); };
  }, [key]);

  const resolved = resolveSections(sections, pool);
  return { resolved, loading };
}
